import { ContentLanguageTranslation } from '../../../types/InternalAnalystComment'
import { getAnalysisOverviewPath } from '../../analysis/helpers/analysisPaths'
import {
  AnalysisCommentMutation,
  GameReviewContent,
  GameReviewField,
  gameReviewFields,
  GameReviewMutation,
  InternalAnalysisComment,
  InternalGameReview,
} from '../types/InternalReview'

export const gamesListPath = '/internal/games'

// A stable empty value: `InputTranslator` resets its dialog whenever the value changes identity.
export const emptyTranslation: ContentLanguageTranslation = { en: '', ja: '', zh: '' }

const withQuery = (path: string, query: Record<string, string | null | undefined>) => {
  const params = new URLSearchParams(Object.entries(query).filter((entry): entry is [string, string] => !!entry[1]))
  return params.toString() ? `${path}?${params}` : path
}

// `analysisId`: opened from that analysis' overview, which Back and a delete return to.
export const getGameReviewEditPath = (appId: number | string, reviewId?: string | null, analysisId?: string | null) =>
  withQuery(`/internal/games/${encodeURIComponent(appId)}/review${reviewId ? `/${encodeURIComponent(reviewId)}` : ''}`, { analysisId })

export const getAnalysisCommentEditPath = ({
  gameId,
  gameVersion,
  commentId,
  analysisId,
}: {
  gameId: string
  gameVersion?: string | null
  commentId?: string | null
  analysisId?: string | null
}) => withQuery(`/internal/games/${encodeURIComponent(gameId)}/comments${commentId ? `/${encodeURIComponent(commentId)}` : ''}`, { gameVersion, analysisId })

export const getEditorLeavePath = (analysisId?: string | null) => (analysisId ? getAnalysisOverviewPath(analysisId) : gamesListPath)

export const gameReviewFieldLabelKeys: Record<GameReviewField, string> = {
  [GameReviewField.GenreAndStyle]: 'internal-analysts:review_genre_and_style',
  [GameReviewField.FeaturesAndProgression]: 'internal-analysts:review_features_and_progression',
  [GameReviewField.Monetization]: 'internal-analysts:review_monetization',
  [GameReviewField.SpecialNotes]: 'internal-analysts:review_special_notes',
}

export type GameReviewFormValues = {
  analystId: string
  published: boolean
  content: GameReviewContent
}

export type AnalysisCommentFormValues = {
  analystId: string
  published: boolean
  comment: ContentLanguageTranslation | null
}

// An untouched field of a new document stays null, as legacy sends it.
export const toGameReviewFormValues = (review?: InternalGameReview | null): GameReviewFormValues => ({
  analystId: review?.analystId ?? '',
  published: !!review?.published,
  content: Object.fromEntries(gameReviewFields.map((field) => [field, review?.content?.[field] ?? null])) as GameReviewContent,
})

export const toGameReviewMutation = (review: InternalGameReview | null | undefined, appId: number, values: GameReviewFormValues): GameReviewMutation => ({
  id: review?.id ?? null,
  appId,
  published: values.published,
  analystId: values.analystId || null,
  content: Object.fromEntries(gameReviewFields.map((field) => [field, values.content[field] ?? null])) as GameReviewContent,
})

export const toAnalysisCommentFormValues = (comment?: InternalAnalysisComment | null): AnalysisCommentFormValues => ({
  analystId: comment?.analystId ?? '',
  published: !!comment?.published,
  comment: comment?.content?.comment ?? null,
})

export const toAnalysisCommentMutation = (
  comment: InternalAnalysisComment | null | undefined,
  gameId: string,
  gameVersion: string,
  values: AnalysisCommentFormValues
): AnalysisCommentMutation => ({
  id: comment?.id ?? null,
  gameId,
  gameVersion,
  published: values.published,
  analystId: values.analystId || null,
  content: { comment: values.comment ?? null },
})

// A missing language and an empty one are the same text.
export const isSameTranslation = (a?: ContentLanguageTranslation | null, b?: ContentLanguageTranslation | null) => {
  const languages = new Set([...Object.keys(a ?? {}), ...Object.keys(b ?? {})])
  return Array.from(languages).every(
    (language) => (a?.[language as keyof ContentLanguageTranslation] ?? '') === (b?.[language as keyof ContentLanguageTranslation] ?? '')
  )
}

export type GameReviewStoredField = 'appId' | 'analyst' | 'published' | GameReviewField

export const getUnstoredGameReviewFields = (sent: GameReviewMutation, stored: InternalGameReview): GameReviewStoredField[] => {
  const unstored: GameReviewStoredField[] = []
  if (stored.appId !== sent.appId) unstored.push('appId')
  if ((stored.analystId ?? null) !== sent.analystId) unstored.push('analyst')
  if (!!stored.published !== sent.published) unstored.push('published')
  gameReviewFields.forEach((field) => {
    if (!isSameTranslation(sent.content[field], stored.content?.[field])) unstored.push(field)
  })
  return unstored
}

export type AnalysisCommentStoredField = 'gameId' | 'gameVersion' | 'analyst' | 'published' | 'comment'

export const getUnstoredAnalysisCommentFields = (sent: AnalysisCommentMutation, stored: InternalAnalysisComment): AnalysisCommentStoredField[] => {
  const unstored: AnalysisCommentStoredField[] = []
  if (stored.gameId !== sent.gameId) unstored.push('gameId')
  if (stored.gameVersion !== sent.gameVersion) unstored.push('gameVersion')
  if ((stored.analystId ?? null) !== sent.analystId) unstored.push('analyst')
  if (!!stored.published !== sent.published) unstored.push('published')
  if (!isSameTranslation(sent.content.comment, stored.content?.comment)) unstored.push('comment')
  return unstored
}

export const storedFieldLabelKeys: Record<GameReviewStoredField | AnalysisCommentStoredField, string> = {
  ...gameReviewFieldLabelKeys,
  appId: 'internal-analysts:review_field_app_id',
  gameId: 'internal-analysts:review_field_game_id',
  gameVersion: 'internal-analysts:review_field_game_version',
  analyst: 'internal-analysts:review_analyst',
  published: 'internal-analysts:review_published',
  comment: 'internal-analysts:comment_comment',
}

export const needsPublishConfirmation = (stored: { published?: boolean } | null | undefined, values: { published: boolean }) =>
  values.published && !stored?.published
